/**
 * Number output shared by the descriptive-statistics widgets.
 *
 * Display goes through the page's `useToolNumber()` formatter, passed in as `fmt`, so a
 * German reader sees `0,4523` and an English one `0.4523`. The clipboard is different:
 * CSV and Markdown leave the page and are re-read by spreadsheets, so `machineNumber`
 * writes ASCII digits with a period and no grouping whatever the locale.
 */

import { t } from "@/lib/i18n/interpolate";

export type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const SCIENTIFIC_BELOW = 1e-6;

/**
 * A statistic as the reader reads it: up to `digits` decimals, switching to four
 * significant figures (and then scientific notation) for values so small that fixed
 * decimals would print them as zero.
 */
export function formatStat(
  fmt: NumberFormatter,
  value: number,
  digits = 4
): string {
  if (Number.isNaN(value)) {
    return "—";
  }
  if (!Number.isFinite(value)) {
    return value > 0 ? "∞" : "−∞";
  }
  if (value === 0) {
    // −0 (from 0 × −3, say) would print as "-0".
    return fmt(0);
  }
  const magnitude = Math.abs(value);
  if (magnitude !== 0 && magnitude < SCIENTIFIC_BELOW) {
    return fmt(value, {
      notation: "scientific",
      maximumSignificantDigits: 4,
    });
  }
  if (magnitude !== 0 && magnitude < 10 ** -digits) {
    return fmt(value, { maximumSignificantDigits: 4 });
  }
  return fmt(value, { maximumFractionDigits: digits });
}

/** A two-sided p-value: four decimals, or "< 0.0001" below that. */
export function formatPValue(fmt: NumberFormatter, p: number): string {
  if (Number.isNaN(p)) {
    return "—";
  }
  if (p < 0.0001) {
    return `< ${fmt(0.0001, { maximumFractionDigits: 4 })}`;
  }
  return fmt(p, { maximumFractionDigits: 4 });
}

/**
 * A p-value with its relation sign, for sentences: "= 0.0346" or "< 0.0001", so a
 * template can say "p {p}" without ever printing "p = < 0.0001".
 */
export function formatPRelation(fmt: NumberFormatter, p: number): string {
  const value = formatPValue(fmt, p);
  return value.startsWith("<") ? value : `= ${value}`;
}

/** A proportion (0–1) written as a percentage in the reader's convention. */
export function formatPercent(
  fmt: NumberFormatter,
  proportion: number,
  digits = 2
): string {
  if (!Number.isFinite(proportion)) {
    return "—";
  }
  return fmt(proportion, {
    style: "percent",
    maximumFractionDigits: digits,
  });
}

const MACHINE_FORMATTERS = new Map<number, Intl.NumberFormat>();

/** ASCII digits, a period, no grouping — for CSV, Markdown and other copied text. */
export function machineNumber(value: number, digits = 6): string {
  if (!Number.isFinite(value)) {
    return "";
  }
  let formatter = MACHINE_FORMATTERS.get(digits);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: digits,
      useGrouping: false,
    });
    MACHINE_FORMATTERS.set(digits, formatter);
  }
  return formatter.format(value);
}

/**
 * A list of numbers for the working ("Sorted data: 12, 15, 17 … and 30 more").
 *
 * The separator comes from the widget's strings, because a comma between numbers that
 * may themselves contain a decimal comma is unreadable in half the locales — those
 * translations write "; ".
 */
export function formatNumberList(
  fmt: NumberFormatter,
  values: readonly number[],
  separator: string,
  moreTemplate: string,
  limit = 40
): string {
  const shown = values
    .slice(0, limit)
    .map((value) => formatStat(fmt, value))
    .join(separator);
  if (values.length <= limit) {
    return shown;
  }
  return `${shown} ${t(moreTemplate, { count: fmt(values.length - limit) })}`;
}
